import React, { useState } from "react";
import Minus from "images/Minus.svg";
import Plus from "images/Plus.svg";
import NcImage from "shared/NcImage/NcImage";

interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="flex justify-between w-full px-4 py-6 text-left items-center"
      >
        <span className="text-lg font-medium leading-7 text-left">{title}</span>
        <span>{isOpen ? <NcImage src={Minus} /> : <NcImage src={Plus} />}</span>
      </button>
      {isOpen && <div className="px-4 py-2">{children}</div>}
    </div>
  );
};

// Content from the client's "Frequently Asked Questions Magnum.docx" (Oct 2026).
const FAQS: { q: string; a: string[] }[] = [
  {
    q: "What is the minimum age to rent a car from Magnum Car Rentals?",
    a: [
      "You must be at least 21 years old to rent a vehicle from Magnum Car Rentals. A valid driver's license and other required identification must also be provided during the booking and verification process.",
    ],
  },
  {
    q: "Do I need my own car insurance to rent from Magnum?",
    a: [
      "Yes. You need to have your own personal auto insurance to rent from Magnum Car Rentals. However, Magnum's rentals include insurance coverage, giving guests the ability to rent from us without active personal car insurance.",
    ],
  },
  {
    q: "Is a security deposit required when renting a car?",
    a: [
      "Yes. Magnum Car Rentals requires a security deposit for rentals. The deposit helps protect against potential charges such as damages, unpaid rental obligations, or other applicable fees. The applicable deposit amount will be communicated during the booking process.",
    ],
  },
  {
    q: "What is the minimum rental period?",
    a: [
      "Magnum Car Rentals accepts rentals starting from one day. However, a one-day rental requires a $250 deposit and a two-day rental requires a $150 deposit. Whether you need a vehicle for a day, a week, or longer, you can book a rental that fits your travel needs. We also have plans for ridesharing rentals.",
    ],
  },
  {
    q: "Where is Magnum Car Rentals located?",
    a: [
      "Magnum Car Rentals is located at 2231 East Oakton Street, Arlington Heights, Illinois 60005, approximately 10 minutes from Chicago O'Hare International Airport. This convenient location makes it easy for travelers arriving in the Chicago area to pick up and return their vehicles.",
    ],
  },
  {
    q: "Can I drive the rental car outside the Chicago area or out of state?",
    a: [
      "Yes, but you should notify Magnum in advance if you plan to drive outside the Chicago area or its suburbs. A deposit of $800 is required.",
    ],
  },
  {
    q: "Can I add another driver to my rental?",
    a: [
      "Yes. Additional drivers can be added free of charge. The additional driver must meet the applicable driver requirements and be properly authorized before driving the vehicle. Magnum's current rental listings specifically include free additional drivers as part of the rental experience.",
    ],
  },
  {
    q: "What happens if I return my rental car late?",
    a: [
      "Magnum provides a 30-minute return grace period. If you are running more than 30 minutes late, you should extend your trip rather than simply keeping the vehicle beyond the agreed return time. This helps prevent conflicts with the next guest's reservation.",
    ],
  },
  {
    q: "Do Magnum rental cars come with roadside assistance?",
    a: [
      "Yes. Magnum provides roadside assistance and customer support. If you experience a breakdown, mechanical issue, or other roadside problem during your rental, you can contact the Magnum support team for assistance.",
    ],
  },
  {
    q: "What are the rules for using and returning a Magnum rental vehicle?",
    a: [
      "Guests are expected to return the vehicle in good condition and follow the rental rules. Smoking is not permitted in Magnum vehicles, and smoking can result in a $150 cleaning/fine charge. Guests should also keep the vehicle reasonably tidy, return it with the required fuel level, and are not permitted to use the vehicle for off-roading. A vehicle returned unreasonably dirty may also incur a $150 cleaning charge.",
    ],
  },
  {
    q: "How many miles are included with my rental?",
    a: [
      "Your rental includes 200 miles per day.",
      "For example, a three-day rental includes 600 miles. Mileage beyond the included allowance is charged at $0.22 per additional mile.",
    ],
  },
  {
    q: "Who is responsible for tolls, tickets, and parking violations?",
    a: [
      "The renter is responsible for all tolls, traffic tickets, parking violations, red-light camera citations, and other violations incurred during the rental.",
      "Magnum vehicles may have a toll transponder, making toll-road travel convenient, but toll usage is still billed to the guest. Applicable administrative or processing fees may also be charged.",
      "Guests should therefore make sure they follow all traffic and parking regulations during their rental.",
    ],
  },
  {
    q: "What should I do if I have an accident or the vehicle is damaged?",
    a: [
      "If an accident occurs, you should immediately notify law enforcement and Magnum Car Rentals.",
      "Guests are responsible for complying with the insurance requirements and reporting accidents promptly. Failure to report an accident may result in additional liability and could affect the guest's deposit.",
    ],
  },
];

const FAQ: React.FC = () => {
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
  };
  return (
    <>
      <div className="flex justify-center mb-16">
        <span className="text-3xl md:text-4xl font-semibold leading-10 text-center tracking-tight">
          FAQ
        </span>
      </div>
      <div className="max-w-3xl mx-auto">
        {FAQS.map(({ q, a }, i) => (
          <Section
            key={q}
            title={q}
            isOpen={openSectionIndex === i}
            onToggle={() => handleToggle(i)}
          >
            {a.map((p) => (
              <p
                key={p}
                className="text-base font-normal leading-6 text-left text-[#6B7280] mb-3 last:mb-0"
              >
                {p}
              </p>
            ))}
          </Section>
        ))}
      </div>
    </>
  );
};

export default FAQ;
